import { Component, inject } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from '@angular/material/dialog';
import {
  CreateTaskDto,
  EntityIdentifierHelper,
  EntityName,
  EntityReference,
  MemberView,
  Task,
  TaskStatus,
  UpdateTaskDto,
} from '@fairandsmart/types';
import { DatePipe, KeyValuePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MomentModule } from 'ngx-moment';
import * as moment from 'moment';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { FsAngularDirectivesModule } from '@fairandsmart/angular/directives';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { OrganisationMemberComponent } from '../../common/organisation-member/organisation-member.component';
import { TaskStatusComponent } from '../task-status/task-status.component';
import { TaskService } from '../task.service';

export type TaskDetailData =
  {
    task?: Task;
    scope?: EntityReference;
    mode?: ViewMode;
    reference?: EntityReference;
    organisationMembers: { [userid: string]: MemberView };
  }
  & ({ task: Task, mode: ViewMode.VIEW | ViewMode.EDIT } | { mode: ViewMode.CREATE, reference: EntityReference, scope: EntityReference });

export enum ViewMode {
  VIEW,
  EDIT,
  CREATE,
}

@Component({
    selector: 'fs-task-detail-dialog',
    templateUrl: './task-detail-dialog.component.html',
    styleUrls: ['./task-detail-dialog.component.scss'],
    imports: [MatDialogTitle, MatIconButton, MatTooltip, MatIcon, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule,
      MatFormField, MatLabel, MatInput, MatSelect, MatOption, FsAngularDirectivesModule, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, OrganisationMemberComponent, MatButton, TaskStatusComponent, MatDialogActions, DatePipe, KeyValuePipe, TranslatePipe, MomentModule]
})
export class TaskDetailDialogComponent {
  readonly dialogRef = inject<MatDialogRef<TaskDetailDialogComponent, boolean>>(MatDialogRef);

  data = inject<TaskDetailData>(MAT_DIALOG_DATA);

  readonly taskService = inject(TaskService);

  mode: ViewMode;

  private _task: Task;

  dueDateMin: Date;

  dueDateDuration: moment.Duration;

  form: UntypedFormGroup;

  message = new UntypedFormControl();

  showCommentForm = false;

  readonly STATUS = TaskStatus;

  readonly MODE = ViewMode;

  constructor() {
    const data = this.data;

    this.task = data.task;
    this.dueDateMin = new Date();
    this.form = new UntypedFormGroup({
      prefix: new UntypedFormControl(this.getPrefix(), [Validators.required, Validators.maxLength(3)]),
      key: new UntypedFormControl(''),
      name: new UntypedFormControl('', Validators.required),
      assignee: new UntypedFormControl('', Validators.required),
      dueDate: new UntypedFormControl('', Validators.required),
      description: new UntypedFormControl(),
    });
    this.mode = data.mode;
  }

  get task(): Task {
    return this._task;
  }

  set task(value: Task) {
    this._task = value;
    if (this._task && this._task.dueDate) {
      this.dueDateDuration = moment.duration(this._task.dueDate - new Date().getTime(), 'ms');
    }
  }

  new(): void {
    this.mode = ViewMode.CREATE;
    this.form.reset();
  }

  edit(): void {
    this.mode = ViewMode.EDIT;
    this.form.setValue({
      prefix: this._task.key.substring(0, 3),
      key: this._task.key,
      name: this._task.name,
      description: this._task.description,
      assignee: this._task.assignee,
      dueDate: new Date(this._task.dueDate),
    });
  }

  create(): void {
    if (this.form.valid) {
      const dueDate: Date = this.form.get('dueDate').value;
      dueDate.setHours(23, 59, 59, 999);
      const dto: CreateTaskDto = {
        prefix: this.form.get('prefix').value,
        name: this.form.get('name').value,
        assignee: this.form.get('assignee').value,
        dueDate: dueDate.getTime(),
        description: this.form.get('description').value,
        scope: this.data.scope,
        reference: this.data.reference,
      };
      this.taskService.create(dto).pipe(
        tap((task: Task) => {
          this.task = task;
          this.mode = ViewMode.VIEW;
        }),
        catchError(() => EMPTY),
      ).subscribe();
    }
  }

  update(): void {
    if (this.form.valid) {
      const dueDate: Date = this.form.get('dueDate').value;
      dueDate.setHours(23, 59, 59, 999);
      const dto: UpdateTaskDto = {
        name: this.form.get('name').value,
        dueDate: dueDate.getTime(),
        description: this.form.get('description').value,
      };
      this.taskService.update(this._task, dto).pipe(
        tap((task: Task) => {
          this.task = task;
          this.mode = ViewMode.VIEW;
        }),
        catchError(() => EMPTY),
      ).subscribe();
    }
  }

  toggleComment(): void {
    this.showCommentForm = !this.showCommentForm;
    if (!this.showCommentForm) {
      this.message.setValue('');
    }
  }

  addComment(): void {
    this.message.disable();
    this.taskService.comment(this._task.id, this.message.value).pipe(
      tap((task: Task) => {
        this.task = task;
        this.message.reset();
      }),
      catchError(() => EMPTY),
      finalize(() => {
        this.message.enable();
        this.showCommentForm = false;
      }),
    ).subscribe();
  }

  goToReference(): void {
    this.dialogRef.close(false);
    this.taskService.goToTaskReference(this._task);
  }

  cancel(): void {
    if (this.mode === ViewMode.CREATE) {
      this.close();
    } else {
      this.mode = ViewMode.VIEW;
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }

  private getPrefix(): string {
    if (this.data.reference) {
      switch (EntityIdentifierHelper.extractType(this.data.reference)) {
        case EntityName.REQUEST:
          return 'REQ';
        case EntityName.CONSENT:
        case EntityName.CONSENT_MODEL:
        case EntityName.CONSENT_OPT_OUT_MODEL:
          return 'CON';
        case EntityName.CONNECTION:
          return 'CNT';
        default:
          return '';
      }
    }
    return '';
  }
}
